/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { setGlobalModals } from '../../redux/app/actions';
import TaskCalendarEventMenu from './components/TaskCalendarOverview/components/TaskCalendarEventMenu';
import { getBarnBoard } from '../../redux/board/actions';
import SelectIcon from '../../images/unused-undraw-illustrations/undraw_select_option_re_u4qn.svg';
import FilterBar from './components/FilterBar';
import Calendar from './components/Calendar';
import TaskCalendarOverview from './components/TaskCalendarOverview';
import { SCHEDULE_MODAL_TYPE } from '../../redux/app/types';
import HorsesCardComponent from '../../pages/settings/LocationSettingsPage/locations/components/HorsesCard';
import { getAllHorseLocations } from '../../redux/location/actions';

function getTaskTypeTags(calendarEvents, horseTasks, barnTasks) {
  // add task type condition if present
  let taskTypeCondition = []
  if (calendarEvents) {
    taskTypeCondition = [...taskTypeCondition, calendarEvents];
  }
  if (horseTasks) {
    taskTypeCondition = [...taskTypeCondition, horseTasks];
  }
  if (barnTasks) {
    taskTypeCondition = [...taskTypeCondition, barnTasks];
  }

  return taskTypeCondition;
}

const PlannerComponent = ({
  calendarEvents,
  horseTasks,
  barnTasks,
  defaultView,
  defaultCalendarView,
  defaultAssignment,
  onPerformedAction: onPerformedActionProp,
  navigate,
  defaultBarnHorses,
  allowChangeView,
  padding,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();

  // React State
  const savedWeekStyle = window.localStorage.getItem('planning_week_style') || 'isoWeek';
  const [weekStyle, setWeekStyle] = React.useState(savedWeekStyle);
  const [startDate, setStartDate] = React.useState((defaultView === 'calendar' && defaultCalendarView === 'month')
    ? moment().startOf('month').toDate()
    : moment().startOf(savedWeekStyle).startOf('day').toDate());
  const [endDate, setEndDate] = React.useState((defaultView === 'calendar' && defaultCalendarView === 'month')
    ? moment().endOf('month').toDate()
    : moment().endOf(savedWeekStyle).endOf('day').toDate());
  const [currentView, setCurrentView] = React.useState(defaultView)
  const [selectedSlot, setSelectedSlot] = React.useState(null);
  const [openQuickSelectMenu, setOpenQuickSelectMenu] = React.useState(false);
  const [selectedBarnHorses, setSelectedBarnHorses] = React.useState(defaultBarnHorses || []);
  const [slotBarnHorses, setSlotBarnHorses] = React.useState([])
  const [tags, setTags] = React.useState([]);
  const [includeCalendarEvents, setIncludeCalendarEvents] = React.useState(calendarEvents);
  const [includeHorseTasks, setIncludeHorseTasks] = React.useState(horseTasks);
  const [includeBarnTasks, setIncludeBarnTasks] = React.useState(barnTasks);
  const [selectedScheduleUser, setSelectedScheduleUser] = React.useState(null);
  const [eventTemplatesFilter, setEventTemplatesFilter] = React.useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = React.useState(null);
  const allBarnHorses = useSelector((state) => state.barn.allBarnHorses);
  const barnHorses = useSelector((state) => state.board.barnHorses);
  const errorMessage = useSelector((state) => state.board.errorMessage);
  const lastUpdatedDate = useSelector((state) => state.board.lastUpdatedDate);
  const isFetchingEventTemplates = useSelector((state) => state.eventTemplate.isFetchingEventTemplates);
  const isFetchingBoard = useSelector((state) => state.board.isFetchingBoard);

  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const eventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const selectedLocation = useSelector((state) => state.location.selectedLocation);
  const realTimeUpdateError = useSelector((state) => state.board.realTimeUpdateError);

  const loadData = (forceBarnHorseRefresh) => {
    // ONly load if tempaltes have loaded
    if (eventTemplates && eventTemplates.length > 0) {
      // Because we cache barn horses, might might need to override that if horses were added on this page
      dispatch(getBarnBoard(
        startDate,
        endDate,
        selectedLocation,
        selectedBarnHorses,
        getTaskTypeTags(includeCalendarEvents && selectedBarn.barnMetadata.eventTaskTypeTagId, includeHorseTasks && selectedBarn.barnMetadata.horseTaskTypeTagId, includeBarnTasks && selectedBarn.barnMetadata.barnTaskTypeTagId),
        tags.map((t) => t.id),
        {
          forceBarnHorseRefresh,
          // eslint-disable-next-line no-nested-ternary
          auth0UserId: selectedScheduleUser
            ? (selectedScheduleUser.auth0UserId || selectedScheduleUser.email || selectedScheduleUser.userFullName)
            : null,
          eventTemplateIds: (eventTemplatesFilter && eventTemplatesFilter.length > 0 && eventTemplatesFilter) || null,
        },
      ));
    }
  }

  const onPerformedAction = () => {
    if (realTimeUpdateError || !lastUpdatedDate || moment().diff(moment(lastUpdatedDate), 'minutes') > 60) {
      // if we have a real time update error or its been over an hour, we need to reload the data
      loadData(true);
    }
    onPerformedActionProp();
  };

  React.useEffect(() => {
    if (defaultAssignment && defaultAssignment.id) {
      // set the default assignment/schedule user if passed in
      setSelectedScheduleUser(defaultAssignment)
    }
  }, [defaultAssignment]);

  React.useEffect(() => {
    setCurrentView(defaultView);
  }, [defaultView])

  React.useEffect(() => {
    setWeekStyle(weekStyle);
    moment.updateLocale('en', {
      week: {
        dow: weekStyle === 'isoWeek'
          ? 1 // First day of week is Monday
          : 0,
        doy: 4, // First week of year must contain 4 January (7 + 1 - 4)
      },
    });
    window.localStorage.setItem('planning_week_style', weekStyle);
    if (currentView !== 'calendar') {
      setStartDate(moment().startOf('week').startOf('day').toDate())
      setEndDate(moment().endOf('week').endOf('day').toDate())
    }
  }, [weekStyle])

  React.useEffect(() => {
    if (selectedLocation && selectedLocation.id) {
      loadData()
    }
  }, [tags, includeCalendarEvents, includeHorseTasks, includeBarnTasks, selectedBarnHorses, selectedScheduleUser, selectedLocation, startDate, endDate, eventTemplates, allBarnHorses, eventTemplatesFilter])

  React.useEffect(() => {
    dispatch(getAllHorseLocations(startDate, endDate));
  }, [startDate])

  const scheduleClick = () => {
    const today = moment().startOf('day').toDate();
    setSelectedSlot({
      start: today,
      end: new Date(today.getTime() + 86400000), // mimicks an all day event
      barnHorses: slotBarnHorses,
    });
    setOpenQuickSelectMenu(true)
  }

  const handleEventClick = ({
    resource: schedule,
    ...event
  }) => {
    dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE, null, schedule, event, {
      locationId: event.eventTask
        ? selectedLocation.id
        : null, // if set, this is a calendar event
      taskTagIds: [],
      eventTemplateTagTypeFilter: selectedBarn.barnMetadata.eventTaskTypeTagId,
      openInSeries: false,
      startDate: null,
    }, (reload) => {
      if (reload) {
        loadData();
      }
    }));
  };

  const handlePlannedAction = (reload, callback) => {
    if (reload) {
      loadData();
    }
    if (callback) {
      callback();
    }
    setSelectedSlot(null);
    onPerformedAction(); // Used by onboarding component to know if user performed an action
  }

  const handleOpenScheduleModal = (eventTemplate, isCalendarEvent, replacementSlot) => {
    let taskTagIds = [];

    const slot = replacementSlot || selectedSlot; // Use passed in slot, otherwise use state slot

    if (eventTemplate.tags.find((t) => t.id === selectedBarn.barnMetadata.barnTaskTypeTagId)) {
      // this is a barn task so add a location
      taskTagIds = [{
        id: parseInt(selectedLocation.pingumTagId, 10),
        tagType: { id: selectedBarn.barnMetadata.locationTagTypeId },
      }]
    } else if (eventTemplate.tags.find((t) => t.id === selectedBarn.barnMetadata.horseTaskTypeTagId || t.id === selectedBarn.barnMetadata.eventTaskTypeTagId)) {
      // horse task so add any selected horses
      taskTagIds = ((slot && slot.barnHorses) || selectedBarnHorses || []).map((bh) => ({
        id: bh.pingumTagId
          ? parseInt(bh.pingumTagId, 10)
          : bh,
        tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
      }));
    }

    const slotDuration = slot && (moment(slot.end).valueOf() - moment(slot.start).valueOf() >= 86400000
      ? null
      : moment(slot.end).valueOf() - moment(slot.start).valueOf());

    dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE, null, null, null, {
      locationId: isCalendarEvent && selectedLocation && selectedLocation.id,
      taskTagIds,
      eventTemplateTagTypeFilter: (eventTemplate && eventTemplate.id)
        ? eventTemplate.id
        : null,
      openInSeries: (slot && slot.openInSeries) || false,
      startDate: slot
        ? moment(slot.start).toDate()
        : moment(startDate).set('hours', moment().get('hour')).startOf('hour').add(1, 'hours')
          .toDate(),
      duration: (slotDuration && slotDuration < 1800000 && slotDuration > 10000)
        ? 1800000
        : slotDuration, // don't allow less than 15 mintues by default
      assignment: selectedScheduleUser,
      assignmentTaskMap: slot && slot.barnHorsesAssignmentMapping, // Used for suggestions or TaskCalendarEventMenu Advanced
      workflows: slot && slot.workflows,
      drawer: slot.drawer || false,
      restOfSuggestions: slot.restOfSuggestions || [],
    }, (reload) => {
      handlePlannedAction(reload, slot && slot.callback);
    }));
  }

  return (
    <Box>
      <FilterBar
        selectedScheduleUser={selectedScheduleUser}
        setSelectedScheduleUser={setSelectedScheduleUser}
        scheduleClick={scheduleClick}
        setIncludeCalendarEvents={setIncludeCalendarEvents}
        setIncludeHorseTasks={setIncludeHorseTasks}
        setIncludeBarnTasks={setIncludeBarnTasks}
        includeCalendarEvents={includeCalendarEvents}
        includeHorseTasks={includeHorseTasks}
        includeBarnTasks={includeBarnTasks}
        setSelectedBarnHorses={setSelectedBarnHorses}
        defaultBarnHorses={defaultBarnHorses}
        selectedBarnHorses={selectedBarnHorses}
        setTags={setTags}
        tags={tags}
        setCurrentView={setCurrentView}
        currentView={currentView}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        weekStyle={weekStyle}
        setWeekStyle={setWeekStyle}
        navigate={navigate}
        allowChangeView={allowChangeView}
        eventTemplatesFilter={eventTemplatesFilter}
        setEventTemplatesFilter={setEventTemplatesFilter}
        selectedCalendarId={selectedCalendarId}
        setSelectedCalendarId={setSelectedCalendarId}
      />
      <Box
        sx={{ m: padding && 2 }}
      >

        {
          (!selectedLocation || !selectedLocation.id) && (
            <Box
              sx={{
                margin: 'auto',
                p: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="captions" align="center" component="div" sx={{ m: 4 }}>
                Select a location to view task overview
              </Typography>
              <img
                src={SelectIcon}
                alt="man selecting something"
                style={{
                  width: 250,
                  display: 'block',
                  margin: 'auto',
                  marginBottom: '1rem',
                }}
              />
            </Box>
          )
        }
        <Box>

          {
            (!isFetchingBoard && !isFetchingEventTemplates && !errorMessage && selectedLocation && selectedLocation.id && barnHorses.length === 0) && (
              <Box
                sx={{
                  textAlign: 'center',
                  m: 4,
                }}
              >
                <InfoOutlined
                  sx={{ fontSize: 50 }}
                  color="secondary"
                />
                <Typography
                  variant="subtitle1"
                  align="center"
                  component="div"
                  sx={{ my: 2 }}
                >
                  No Horses At This Location
                </Typography>
              </Box>
            )
          }
          {
            (selectedLocation && selectedLocation.id && currentView === 'calendar')
            && (
              <Calendar
                handleEventClick={handleEventClick}
                setSelectedSlot={setSelectedSlot}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                setOpenQuickSelectMenu={setOpenQuickSelectMenu}
                defaultCalendarView={defaultCalendarView}
              />
            )
          }

          {
            (selectedLocation && selectedLocation.id && currentView === 'grid') && (
              <TaskCalendarOverview
                navigate={navigate}
                handleRefresh={loadData}
                handleOpenScheduleModal={handleOpenScheduleModal}
                selectedScheduleUser={selectedScheduleUser}
                setOpenQuickSelectMenu={setOpenQuickSelectMenu}
                setSelectedSlot={setSelectedSlot}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                startDate={startDate}
                endDate={endDate}
                setSlotBarnHorses={setSlotBarnHorses}
                loadData={loadData}
              />
            )
          }
          <Box sx={{ mt: 2 }}>
            {(selectedLocation && selectedLocation.id && currentView !== 'grid') && (
              <HorsesCardComponent
                location={selectedLocation}
                onlyButton
                variant="text"
                icon
                onHorsesAdded={() => {
                  setTimeout(() => {
                    loadData(true)
                  }, 500)
                }}
              />
            )}
            {
              allBarnHorses.length <= 0 && !isFetchingBoard && (
                <Button size="small" variant="contained" onClick={() => navigate('/horses')}>Create and manage horses</Button>
              )
            }
          </Box>
        </Box>
      </Box>
      <TaskCalendarEventMenu
        tagsFilter={tags || []}
        includeBarnTasks={includeBarnTasks && (!selectedSlot || !selectedSlot.isHorseTask)}
        includeHorseTasks={includeHorseTasks && (!selectedSlot || !selectedSlot.isBarnTask)}
        open={openQuickSelectMenu}
        onClose={(plannedAction, reload, callback) => {
          setOpenQuickSelectMenu(false); setSelectedSlot(null);
          if (selectedSlot && selectedSlot.callback) {
            selectedSlot.callback();
          }
          if (plannedAction) {
            handlePlannedAction(reload || false, callback)
          }
        }}
        onTaskSelect={(et, replacementSlot) => {
          setOpenQuickSelectMenu(false);
          if (selectedSlot.selectTaskTypeActionReplacement) {
            selectedSlot.selectTaskTypeActionReplacement(et);
          } else {
            handleOpenScheduleModal(et, false, replacementSlot);
          }
        }}
        onCalendarEventSelect={(et) => { setOpenQuickSelectMenu(false); handleOpenScheduleModal(et, true); }}
        selectedSlot={{
          ...selectedSlot,
          assignment: selectedScheduleUser,
        }}
      />
    </Box>
  );
};

PlannerComponent.propTypes = {
  calendarEvents: PropTypes.bool,
  horseTasks: PropTypes.bool,
  barnTasks: PropTypes.bool,
  defaultView: PropTypes.string,
  defaultAssignment: PropTypes.object,
  onPerformedAction: PropTypes.func,
  navigate: PropTypes.func.isRequired,
  defaultBarnHorses: PropTypes.array,
  defaultCalendarView: PropTypes.string,
  allowChangeView: PropTypes.bool,
  padding: PropTypes.bool,
};

PlannerComponent.defaultProps = {
  calendarEvents: true,
  horseTasks: true,
  barnTasks: true,
  defaultView: 'grid',
  defaultAssignment: null,
  onPerformedAction: () => { },
  defaultBarnHorses: null,
  defaultCalendarView: null,
  allowChangeView: true,
  padding: false,
};

export default (PlannerComponent);

